import {Radio as RadioPrimitive} from '@base-ui/react/radio';
import {RadioGroup as RadioGroupPrimitive} from '@base-ui/react/radio-group';

import {cn} from '../../lib/utils';
import {Tooltip} from '../Tooltip';

import type {ReactNode} from 'react';

export interface SegmentedControlOption {
  value: string;
  label: string;
  disabled?: boolean;
  tooltip?: ReactNode;
}

export interface SegmentedControlProps {
  options: SegmentedControlOption[];
  value: string;
  onValueChange: (value: string) => void;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  className?: string;
}

/**
 * A compact, content-width single choice: a radio group drawn as a row of small segments, the
 * picked one filled with the brand colour.
 */
export function SegmentedControl({
  options,
  value,
  onValueChange,
  className,
  ...ariaProps
}: SegmentedControlProps) {
  return (
    <RadioGroupPrimitive
      data-slot="segmented-control"
      value={value}
      onValueChange={next => onValueChange(String(next))}
      className={cn(
        'inline-flex w-fit items-center gap-0.5 rounded-md border border-border bg-background-surface p-0.75',
        className
      )}
      {...ariaProps}
    >
      {options.map(option => (
        <Tooltip key={option.value} content={option.tooltip}>
          <RadioPrimitive.Root
            value={option.value}
            disabled={option.disabled}
            data-slot="segmented-control-item"
            className={cn(
              'inline-flex h-7 cursor-pointer items-center justify-center rounded-sm px-3 text-xs font-semibold whitespace-nowrap text-fg-secondary transition-colors select-none',
              'outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring',
              'data-checked:bg-brand-primary data-checked:text-fg_on-brand',
              'data-disabled:cursor-not-allowed data-disabled:opacity-45'
            )}
          >
            {option.label}
          </RadioPrimitive.Root>
        </Tooltip>
      ))}
    </RadioGroupPrimitive>
  );
}
